import * as React from 'react';
import styled from 'styled-components';
import { ModalDefaultDraggable } from '../molecules';
import {
  editFormSetup,
  FormSetupType,
} from '../api/formSetup';
import FormSetupPlaceholders from './FormSetupPlaceholders';
import { getActiveFieldsFromMarkup } from '../utils/formSetupUtils';
// Imported directly, NOT from the molecules barrel — that import white-screens
// the app (see CLAUDE.md).
import ModalInfo from '../molecules/ModalInfo';
import { convertErrorResponseToValid } from '../utils/parseErrorResponse';

const EditFormSetupPlaceholders = ({
  data,
  setData,
  updateTable,
  setSelectedFontSize,
  selectedFontSize,
}: Props) => {
  const [isInitData, setInitData] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string>('');
  const [
    initialActiveFields,
    setInitialActiveFields,
  ] = React.useState([]);

  React.useEffect(() => {
    if (data) {
      const result = [];
      (data?.formMarkup || []).forEach(field => {
        getActiveFieldsFromMarkup(field, ['fields'], result);
      });
      setInitialActiveFields(result);
      // Adopt the form's own font size. It used to be left at the page's
      // default of 12, and the select never fires onChange when untouched, so
      // simply dragging a box and saving rewrote a form set to 8/10/14.
      if (data?.fontSize) {
        setSelectedFontSize(+data.fontSize);
      }
      setInitData(true);
    }
  }, [data]);

  // Resolves true when the markup reached the server. The editor uses that to
  // grey its Save button out, and to leave it enabled for a retry on failure.
  const handleSubmitPlaceholders = ({ createdMarkup }) => {
    setSaveError('');
    return editFormSetup({
      formMarkup: JSON.stringify(createdMarkup),
      id: data.id,
      fontSize:
        +data?.fontSize === selectedFontSize
          ? data?.fontSize
          : selectedFontSize,
    })
      .then(() => {
        // Deliberately does NOT close the modal. Placing placeholders is
        // iterative — save, look at it, nudge, save again — and closing on
        // every save meant reopening the form to carry on. The modal is
        // dismissed with its own close button.
        updateTable();
        return true;
      })
      .catch(err => {
        // This used to be `console.log(err)`. The server rejects invalid markup
        // with a 400, so a failed save looked exactly like a successful one
        // that did nothing — the modal just sat there and the move was lost.
        // Keep the modal open so the work isn't thrown away.
        const body = err?.response?.data;
        const parsed = convertErrorResponseToValid(body);
        const message = Object.values(parsed || {})
          .map(value => (Array.isArray(value) ? value.join(' ') : `${value}`))
          .filter(Boolean)
          .join(' ');
        setSaveError(
          message || 'Could not save the placeholders. Please try again.',
        );
        return false;
      });
  };

  return (
    <>
      {isInitData && (
        <StyledModalDraggable
          onClose={() => {
            setData(false);
            setInitData(false);
            setInitialActiveFields([]);
          }}
          title="Set Placeholders"
          wide
        >
          <FormSetupPlaceholders
            url=""
            type={data.type}
            setPlaceholders={setData}
            initialValues={data}
            initialActiveFields={initialActiveFields}
            handleSubmitPlaceholders={
              handleSubmitPlaceholders
            }
            setSelectedFontSize={setSelectedFontSize}
            selectedFontSize={selectedFontSize}
          />
        </StyledModalDraggable>
      )}
      {saveError && (
        <ModalInfo
          isError
          title="Placeholders not saved"
          message={saveError}
          onClose={() => setSaveError('')}
        />
      )}
    </>
  );
};

interface Props {
  data: FormSetupType;
  setData: (data: boolean) => void;
  setSelectedFontSize: (data: any) => void;
  selectedFontSize: number;
  updateTable: () => void;
}

const StyledModalDraggable = styled(ModalDefaultDraggable)`
  height: 100%;
  overflow: inherit;
`;

export default EditFormSetupPlaceholders;
